import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PublicShell } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { NewsletterInline } from "@/components/Newsletter";
import { ShowCover, SHOW_NAME, SHOW_HOST } from "@/components/podcast/common";
import { LearnCard } from "@/components/learn/common";
import api from "@/lib/api";

// No approved portrait exists yet. Set this to a real image URL to switch the hero
// to two columns; while empty the hero renders single-column with no empty box.
const PORTRAIT_SRC = "";

function Rail({ n, label }) {
  return (
    <div className="mb-4 lg:mb-0 lg:w-[140px] lg:flex-shrink-0">
      <span className="kicker text-xs text-brass">{n}</span>
      <span className="kicker ml-3 text-xs text-mutedink lg:ml-0 lg:mt-1 lg:block">{label}</span>
    </div>
  );
}

const PILLARS = [
  ["Jewellery", "The trade from inside it — manufacturing, retail, compliance, and Jewellery Sahi Hai, the industry coalition I lead."],
  ["Ecommerce", "Selling in India on Amazon, Flipkart and quick commerce. What the marketplaces actually reward, and what they quietly punish."],
  ["Building in public", "The brands, the systems and the tools as they get built, with the parts that broke left in."],
];

function PodcastCard() {
  const { data } = useQuery({ queryKey: ["podcast", 1], queryFn: async () => (await api.get("/podcast?page=1")).data, staleTime: 300000 });
  const show = data?.show || {};
  return (
    <div className="flex items-center gap-5 rounded-[10px] border border-border bg-card p-5" data-testid="home-podcast-card">
      <ShowCover src={show.image_url} className="w-24 flex-shrink-0 sm:w-28" testid="home-podcast-cover" />
      <div className="min-w-0">
        <h3 className="font-heading text-xl font-bold text-ink">{SHOW_NAME}</h3>
        <p className="mt-1 text-sm text-muted">A podcast on the Indian jewellery trade, hosted by {SHOW_HOST}.</p>
        <Link to="/podcast" className="mt-3 inline-block text-sm font-semibold text-accent underline-offset-4 hover:underline" data-testid="home-podcast-listen">Listen →</Link>
      </div>
    </div>
  );
}

function LearnStrip() {
  const { data } = useQuery({ queryKey: ["home-learn"], queryFn: async () => (await api.get("/learn?limit=3")).data, staleTime: 60000 });
  const items = data?.items || [];
  if (!items.length) return null;
  return (
    <section className="border-b border-rule" data-testid="home-learn-strip">
      <div className="mx-auto max-w-[1040px] px-5 py-16 lg:px-8">
        <div className="flex items-end justify-between gap-4">
          <h2 className="font-heading text-2xl font-bold text-ink">Learn with @bapnadigital</h2>
          <Link to="/learn" className="whitespace-nowrap text-sm font-semibold text-accent hover:underline" data-testid="home-learn-see-all">See all →</Link>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3">
          {items.map((it) => <LearnCard key={it.id} item={it} testid={`home-learn-${it.external_id}`} />)}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const { data: posts } = useQuery({ queryKey: ["home-desk"], queryFn: async () => (await api.get("/blog?desk=true")).data });
  const latest = (posts || []).slice(0, 3);
  const hasPortrait = !!PORTRAIT_SRC;

  return (
    <PublicShell>
      {/* Hero */}
      <section className="border-b border-border">
        <div className={`mx-auto max-w-[1100px] px-5 py-20 lg:px-8 ${hasPortrait ? "lg:grid lg:grid-cols-[1.1fr_0.9fr] lg:items-center lg:gap-16" : ""}`}>
          <div>
            <p className="kicker text-xs text-accent" data-testid="hero-eyebrow">JEWELLERY · ECOMMERCE · BUILDING IN PUBLIC</p>
            <h1 className="mt-5 font-heading text-[clamp(2.4rem,7vw,4rem)] font-extrabold leading-[1.05] text-ink" data-testid="hero-title">
              I run the businesses I write about.
            </h1>
            <p className="mt-6 max-w-xl text-lg text-muted" data-testid="hero-sub">
              Second-generation jeweller and D2C operator. Everything here comes out of work I am actually doing — in the jewellery trade, in Indian ecommerce, and in building the brands around both.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
              <Button onClick={() => navigate("/podcast")} data-testid="hero-primary-btn" className="rounded-lg bg-accent px-6 py-3 text-white hover:opacity-90">Listen to Jewellery Sahi Hai</Button>
              <Link to="/about" data-testid="hero-secondary-link" className="text-sm font-semibold text-ink underline-offset-4 hover:text-accent hover:underline">About</Link>
            </div>
          </div>
          {hasPortrait && (
            <div className="mt-10 lg:mt-0" data-testid="hero-portrait">
              <img src={PORTRAIT_SRC} alt="Shubham Bapna" className="w-full rounded-xl border border-border object-cover" />
            </div>
          )}
        </div>
      </section>

      {/* Three pillars */}
      <section className="border-b border-rule">
        <div className="mx-auto flex max-w-[1040px] flex-col px-5 py-16 lg:flex-row lg:px-8">
          <Rail n="01" label="What I work on" />
          <div className="grid flex-1 divide-y divide-rule sm:grid-cols-3 sm:divide-x sm:divide-y-0">
            {PILLARS.map(([h, detail], i) => (
              <div key={h} className={`py-6 sm:py-0 ${i === 0 ? "sm:pr-6" : i === 1 ? "sm:px-6" : "sm:pl-6"}`} data-testid={`pillar-${i}`}>
                <span className="kicker text-xs text-brass">0{i + 1}</span>
                <h3 className="mt-2 font-heading text-lg font-bold text-ink">{h}</h3>
                <p className="mt-2 text-sm text-bodytext">{detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <LearnStrip />

      {/* Podcast */}
      <section className="border-b border-rule">
        <div className="mx-auto flex max-w-[1040px] flex-col px-5 py-16 lg:flex-row lg:px-8">
          <Rail n="02" label="Podcast" />
          <div className="flex-1"><PodcastCard /></div>
        </div>
      </section>

      {/* Articles */}
      <section className="border-b border-rule">
        <div className="mx-auto flex max-w-[1040px] flex-col px-5 py-16 lg:flex-row lg:px-8">
          <Rail n="03" label="Articles" />
          <div className="flex-1">
            <div className="flex items-end justify-between">
              <h2 className="font-heading text-2xl font-bold text-ink">Articles</h2>
              <Link to="/blog" className="text-sm font-semibold text-accent hover:underline" data-testid="home-read-archive">Read the archive →</Link>
            </div>
            <div className="mt-6 divide-y divide-rule border-y border-rule" data-testid="home-desk-list">
              {latest.length === 0 && <p className="py-4 text-mutedink">The first stories are landing shortly.</p>}
              {latest.map((p) => (
                <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`home-desk-${p.id}`} className="group block py-4">
                  <h3 className="font-heading text-lg font-semibold text-ink group-hover:text-accent">{p.title}</h3>
                  <p className="mt-1 line-clamp-1 text-sm text-bodytext">{p.dek || p.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section>
        <div className="mx-auto flex max-w-[1040px] flex-col px-5 py-16 lg:flex-row lg:px-8">
          <Rail n="04" label="Newsletter" />
          <div className="flex-1 rounded-[3px] border border-rule bg-card p-6" data-testid="home-newsletter">
            <p className="font-heading text-base font-semibold text-ink">Get it in your inbox, free.</p>
            <div className="mt-4">
              <NewsletterInline source="home-footer" promise="New podcast episodes and new articles, by email when they go out."
                doneText="We've sent a one-click confirmation link. Confirm it and you're on the list." finePrint="Free. Unsubscribe in one click." />
            </div>
          </div>
        </div>
      </section>
    </PublicShell>
  );
}
